// Map from cart contents to the richer model used for editing
// We may be restoring from an old kit that is no longer valid in the current catalog (for reordering old
// kits), so we only overwrite the default state with data we've validated against the catalog.
import { Plexity } from "../../KitDeserializer";
import { findOrganismByName, isPredictedTarget, targetHasMIValidatedEntry } from "../KitToFormStateUtilityFunctions";
import { SimpleCatalogVariantProbeSet, SimpleLibraryOrganismCustomTargetProbeSet } from "../rna-fish/KitModel";
import { extractNameBeforeParenthesis } from "../rna-fish/RnaFishKitToFormState";
import { getProbeAmps } from "./EditKitForm";
import { defaultProKitDetectFormState, defaultProKitProbeFormState } from "./NewMultiPlexProKitPage";
import { RnaCishEditableOrganism, RnaCishEditableTarget } from "./ProbeSetForm";
import { getAmpCatalog } from "./RnaCishKitToAdminFormState";
/**
 * Converts a kit to a consumer form state. If the kit in question does not contain only catalog items such as
 * entries components and scales, then we revert to the default consumer form state settings.
 * @return RnaCishConsumerEditKitForm
 */
export var automatedProKitToProFormState = function (catalog, kit) {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r;
    var proAmplifier = catalog.proAmplifier;
    var amplifierCatalog = getAmpCatalog(false, kit.plexity, proAmplifier);
    var proKitProbeState = [];
    var proKitDetectState = [];
    var probeAmps = getProbeAmps(amplifierCatalog, kit.platform);
    for (var i = 1; i <= kit.plexity; i++) {
        proKitProbeState = proKitProbeState.concat([
            {
                state: defaultProKitProbeFormState((_b = (_a = probeAmps[i - 1]) === null || _a === void 0 ? void 0 : _a.data) !== null && _b !== void 0 ? _b : catalog.proAmplifier.defaultHcrAmplifier),
                metadata: undefined,
                channel: i
            }
        ]);
        proKitDetectState = proKitDetectState.concat([
            {
                state: defaultProKitDetectFormState(),
                metadata: undefined,
                channel: i
            }
        ]);
    }
    if (kit.probeSetComponent.length > 0) {
        proKitProbeState = kit.probeSetComponent.map(function (component) {
            var _a = probeToFormState(catalog, component.probeSet), organism = _a.organism, target = _a.target, targets = _a.targets;
            return {
                state: {
                    organism: organism,
                    target: target,
                    probeSetFamilies: targets,
                    hcrAmplifier: component.probeSet.hcrAmplifier,
                    searchQuery: "",
                    searchLoading: false,
                    enableCustomTarget: true
                },
                channel: component.channel,
                metadata: component.presetScale
            };
        });
    }
    if (((_c = kit.detectComponent) === null || _c === void 0 ? void 0 : _c.channels) != null && kit.detectComponent.channels.length > 0) {
        proKitDetectState = kit.detectComponent.channels.map(function (channelData) {
            var _a;
            return ({
                state: {
                    chromogen: (_a = kit.chromogenComponent) === null || _a === void 0 ? void 0 : _a.chromogen,
                    enzymeCompatibility: detectToFormState(kit.platform, kit.plexity, kit.detectComponent, channelData.channel, proAmplifier)
                },
                channel: channelData.channel,
                metadata: undefined
            });
        });
    }
    var scale = (_l = (_j = (_f = (_e = (_d = kit.probeSetComponent[0]) === null || _d === void 0 ? void 0 : _d.presetScale) === null || _e === void 0 ? void 0 : _e.units) !== null && _f !== void 0 ? _f : (_h = (_g = kit.detectComponent) === null || _g === void 0 ? void 0 : _g.presetScale) === null || _h === void 0 ? void 0 : _h.units) !== null && _j !== void 0 ? _j : (_k = kit.detectComponent) === null || _k === void 0 ? void 0 : _k.customScale) !== null && _l !== void 0 ? _l : (_m = kit.probeSetComponent[0]) === null || _m === void 0 ? void 0 : _m.customScale;
    return {
        kitConfiguration: kit.config,
        plexity: kit.plexity,
        protocolType: kit.platform,
        scale: scale,
        addedSecondTarget: kit.plexity === Plexity.TwoPlex,
        amplifierCatalog: amplifierCatalog,
        comment: kit.comment,
        errors: [],
        prefilled: (_p = (_o = kit.detectComponent) === null || _o === void 0 ? void 0 : _o.prefilled) !== null && _p !== void 0 ? _p : false,
        priceLoading: false,
        proKitDetectState: proKitDetectState,
        proKitProbeState: proKitProbeState,
        chromogenPresetScale: (_q = kit.chromogenComponent) === null || _q === void 0 ? void 0 : _q.presetScale,
        detectPresetScale: (_r = kit.detectComponent) === null || _r === void 0 ? void 0 : _r.presetScale
    };
};
var probeToFormState = function (catalog, probeSet) {
    var organism = RnaCishEditableOrganism.default();
    var target = RnaCishEditableTarget.default(-1);
    var targets = [];
    // Since a consumer will only be able to purchase catalog probe sets we should only be converting the kit to
    // a consumer form state if it contains a catalog entry ref probe set.
    if (probeSet instanceof SimpleCatalogVariantProbeSet) {
        var customerProvidedOrganism = findOrganismByName(extractNameBeforeParenthesis(probeSet.customerOrganismName), catalog);
        if (customerProvidedOrganism != null) {
            var customerProvidedTarget = customerProvidedOrganism.probeSetFamilies.find(function (psf) {
                return psf.geneSymbol.trim().toLowerCase()
                    === extractNameBeforeParenthesis(probeSet.customerTargetName).trim().toLowerCase();
            });
            if (customerProvidedTarget != null) {
                var customerProvidedVariant = customerProvidedTarget.variants.find(function (v) {
                    var accession = v.accession ? v.accession.transcriptAccession : "";
                    return v.targetMrnaSeq === probeSet.customerSequence
                        && v.probeSetSize === probeSet.customerProbeSetSize
                        && accession.trim().toLowerCase() === probeSet.customerAccessionNum.trim().toLowerCase();
                });
                if (customerProvidedVariant != null) {
                    organism = RnaCishEditableOrganism.fromLibOrg(customerProvidedOrganism);
                    target = RnaCishEditableTarget.fromLibTarget(customerProvidedVariant);
                    targets = [
                        {
                            id: customerProvidedVariant.probeSetFamily.id,
                            geneSymbol: customerProvidedVariant.probeSetFamily.geneSymbol,
                            description: customerProvidedVariant.probeSetFamily.description,
                            synonyms: customerProvidedVariant.probeSetFamily.synonyms
                        }
                    ];
                }
                else {
                    organism = RnaCishEditableOrganism.fromLibOrg(customerProvidedOrganism);
                    target = new RnaCishEditableTarget(customerProvidedTarget.geneSymbol, customerProvidedTarget.description, probeSet.customerAccessionNum, probeSet.customerSequence, probeSet.customerProbeSetSize, customerProvidedTarget.id, targetHasMIValidatedEntry(customerProvidedTarget), isPredictedTarget(customerProvidedTarget), undefined);
                    targets = [
                        {
                            id: customerProvidedTarget.id,
                            geneSymbol: customerProvidedTarget.geneSymbol,
                            description: customerProvidedTarget.description,
                            synonyms: customerProvidedTarget.synonyms
                        }
                    ];
                }
            }
            else {
                organism = RnaCishEditableOrganism.fromLibOrg(customerProvidedOrganism);
                target = new RnaCishEditableTarget(probeSet.customerTargetName, "", probeSet.customerAccessionNum, probeSet.customerSequence, probeSet.customerProbeSetSize, -1, false, false, undefined);
            }
        }
        else {
            organism = new RnaCishEditableOrganism(probeSet.customerOrganismName, "", -1);
            target = new RnaCishEditableTarget(probeSet.customerTargetName, "", probeSet.customerAccessionNum, probeSet.customerSequence, probeSet.customerProbeSetSize, -1, false, false, undefined);
        }
        var variant_1 = probeSet.variant;
        var family_1 = probeSet.probeSetFamily;
        var simpleOrganism_1 = probeSet.organism;
        // Look for the entry. Since the consumer kits that a normal consumer will see will only contain catalog
        // probe sets we either match the entry id with one of the catalog entry ids, or we find the equivalent entry
        // if a new batch was uploaded based off detect, variant, geneSymbol, probe set size and
        // organism1
        catalog.probeSet.catalog.organisms.forEach(function (o) {
            o.probeSetFamilies.forEach(function (psf) {
                psf.variants.forEach(function (v) {
                    if (v.id === variant_1.id
                        || (v.probeSetSize === variant_1.probeSetSize
                            && v.probeSetFamily.geneSymbol === family_1.geneSymbol
                            && v.organism.name === simpleOrganism_1.name
                            && v.name === variant_1.name)) {
                        target = target.withProbeDesign(v.id);
                    }
                });
            });
        });
    }
    else if (probeSet instanceof SimpleLibraryOrganismCustomTargetProbeSet) {
        catalog.probeSet.catalog.organisms.forEach(function (o) {
            if (o.id === probeSet.organism.id) {
                organism = RnaCishEditableOrganism.fromLibOrg(o);
                target = new RnaCishEditableTarget(probeSet.target.name, "", probeSet.target.accession, probeSet.target.targetMrnaSeq, probeSet.probeSetSize, -1, false, false, undefined);
            }
        });
    }
    else {
        organism = new RnaCishEditableOrganism(probeSet.organism.name, "", -1);
        target = new RnaCishEditableTarget(probeSet.target.name, "", probeSet.target.accession, probeSet.target.targetMrnaSeq, probeSet.probeSetSize, -1, false, false, undefined);
    }
    return {
        organism: organism,
        target: target,
        targets: targets
    };
};
var detectToFormState = function (platform, plexity, detectComponent, channel, catalog) {
    var ampCatalog = plexity === Plexity.TwoPlex
        ? catalog.twoTargetProAmps
        : catalog.oneTargetProAmps;
    var channelData = detectComponent.channels.find(function (c) { return c.channel === channel; });
    if (!channelData) {
        return undefined;
    }
    return ampCatalog.proAmpConfigList.find(function (config) {
        var _a, _b, _c;
        return config.enzyme.id === ((_a = channelData.enzyme) === null || _a === void 0 ? void 0 : _a.id) &&
            config.amplifier.id === ((_b = channelData.hcrAmplifier) === null || _b === void 0 ? void 0 : _b.id) &&
            config.hapten.id === ((_c = channelData.hapten) === null || _c === void 0 ? void 0 : _c.id) &&
            config.channelNum === channel &&
            config.platformCompatibility.some(function (pc) { return pc.id === platform.id; });
    });
};
//# sourceMappingURL=RnaCishKitToProFormState.js.map


//////////////////
// WEBPACK FOOTER
// ../common/build/dist/kit/automated-pro/RnaCishKitToProFormState.js
// module id = 597
// module chunks = 0